<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/tests/edit/${testId}`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
        <button v-if="!questions.length" class="btn btn-outline-primary btn-sm" @click="openPopulateModal">
          <vue-feather type="cpu" size="14" class="me-1" />
          Populate Questions
        </button>
        <button class="btn btn-primary btn-sm" @click="openCreateModal">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Question
        </button>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Test Questions</h6>
        <small class="text-muted" v-if="test">{{ test.title }}</small>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th style="width: 50px">#</th>
                <th>Question</th>
                <th>Type</th>
                <th>Marks</th>
                <th>Difficulty</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(q, index) in questions" :key="q.id">
                <td>{{ q.order || index + 1 }}</td>
                <td>
                  <div 
                    class="question-preview" 
                    v-tooltip="{ text: stripHtml(q.questionText || q.content), position: 'top' }"
                  >
                    {{ truncate(stripHtml(q.questionText || q.content), 60) }}
                  </div>
                </td>
                <td><span class="badge bg-info">{{ formatType(q.type || q.questionType) }}</span></td>
                <td>{{ q.marks || 1 }}</td>
                <td>
                  <span :class="['badge', getDifficultyClass(q.difficulty)]">{{ q.difficulty || '-' }}</span>
                </td>
                <td class="text-end">
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-info" @click="openEditModal(q)" title="Edit">
                      <vue-feather type="edit" size="14" />
                    </button>
                    <button class="btn btn-outline-danger" @click="confirmDelete(q)" title="Delete">
                      <vue-feather type="trash-2" size="14" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!questions.length">
                <td colspan="6" class="text-center text-muted py-4">
                  <vue-feather type="help-circle" size="32" class="mb-2 opacity-50" />
                  <p class="mb-2">No questions found.</p>
                  <div class="d-flex gap-2 justify-content-center">
                    <button class="btn btn-primary btn-sm" @click="openPopulateModal">
                      <vue-feather type="cpu" size="14" class="me-1" />
                      Populate Questions
                    </button>
                    <button class="btn btn-outline-primary btn-sm" @click="openCreateModal">
                      <vue-feather type="plus" size="14" class="me-1" />
                      Add Manually
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Question' : 'Add Question'"
      icon="help-circle"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.content || !form.question_type"
      :item-id="isEditing ? editingId : ''"
      @submit="saveQuestion"
    >
      <form @submit.prevent="saveQuestion" class="modal-form">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Question Type <span class="text-danger">*</span></label>
            <select v-model="form.question_type" class="form-control" required>
              <option value="">Select Type</option>
              <option value="mcq">Multiple Choice (MCQ)</option>
              <option value="numerical">Numerical</option>
              <option value="match">Match</option>
              <option value="subjective">Subjective</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Difficulty</label>
            <select v-model="form.difficulty" class="form-control">
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Question <span class="text-danger">*</span></label>
          <textarea 
            v-model="form.content" 
            class="form-control" 
            rows="3"
            placeholder="Enter your question..."
            required
          ></textarea>
        </div>

        <!-- MCQ Options -->
        <div v-if="form.question_type === 'mcq'" class="options-section">
          <label class="form-label">Options</label>
          <div class="options-grid">
            <div class="option-input">
              <span class="option-label">A</span>
              <input v-model="form.option_a" type="text" class="form-control" placeholder="Option A" />
            </div>
            <div class="option-input">
              <span class="option-label">B</span>
              <input v-model="form.option_b" type="text" class="form-control" placeholder="Option B" />
            </div>
            <div class="option-input">
              <span class="option-label">C</span>
              <input v-model="form.option_c" type="text" class="form-control" placeholder="Option C" />
            </div>
            <div class="option-input">
              <span class="option-label">D</span>
              <input v-model="form.option_d" type="text" class="form-control" placeholder="Option D" />
            </div>
          </div>

          <div class="form-group mt-3">
            <label class="form-label">Correct Answer</label>
            <select v-model="form.correct_answer" class="form-control">
              <option value="">Select Correct Answer</option>
              <option value="a">Option A</option>
              <option value="b">Option B</option>
              <option value="c">Option C</option>
              <option value="d">Option D</option>
            </select>
          </div>
        </div>

        <!-- Non-MCQ Answer -->
        <div v-else-if="form.question_type" class="form-group">
          <label class="form-label">Answer</label>
          <textarea 
            v-model="form.correct_answer" 
            class="form-control" 
            rows="2"
            placeholder="Enter the correct answer..."
          ></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Explanation</label>
          <textarea 
            v-model="form.explanation" 
            class="form-control" 
            rows="2"
            placeholder="Explain why this is the correct answer..."
          ></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Shortcut Method</label>
          <textarea 
            v-model="form.shortcut_method" 
            class="form-control" 
            rows="2"
            placeholder="Quick trick or shortcut to solve this question..."
          ></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Marks</label>
          <input 
            v-model.number="form.marks" 
            type="number" 
            class="form-control" 
            placeholder="1"
            min="1"
          />
        </div>
      </form>
    </CreateModal>

    <!-- Populate Questions Modal -->
    <PopulateQuestionsModal
      v-model="showPopulateModal"
      :test-id="testId"
      :parent-type="parentType"
      :parent-id="parentId"
      :parent-name="parentName"
      @success="onPopulateSuccess"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'
import CreateModal from '@/components/modals/CreateModal.vue'
import PopulateQuestionsModal from '@/components/modals/PopulateQuestionsModal.vue'


export default {
  name: 'TestQuestions',
  components: { CreateModal, PopulateQuestionsModal },
  setup() {
    const route = useRoute()
    const alert = useAlert()
    const testId = route.params.testId

    const test = ref(null)
    const questions = ref([])
    
    // Modal state
    const showModal = ref(false)
    const showPopulateModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    
    // Parent info for populate modal
    const parentType = computed(() => {
      if (test.value?.seriesId) return 'series'
      if (test.value?.topicId) return 'topic'
      if (test.value?.lessonId) return 'lesson'
      return null
    })
    
    const parentId = computed(() => {
      if (test.value?.seriesId) return test.value.seriesId
      if (test.value?.topicId) return test.value.topicId
      if (test.value?.lessonId) return test.value.lessonId
      return null
    })
    
    const parentName = computed(() => {
      if (test.value?.seriesName) return test.value.seriesName
      if (test.value?.topicName) return test.value.topicName
      if (test.value?.lessonTitle) return test.value.lessonTitle
      return ''
    })
    
    const form = reactive({
      question_type: 'mcq',
      content: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      correct_answer: '',
      explanation: '',
      shortcut_method: '',
      difficulty: 'medium',
      marks: 1
    })

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      // Add parent context based on test type
      if (test.value?.lessonId) {
        // Exam → Subject → Topic → Lesson → Test → Questions
        if (test.value?.examId) {
          items.push({ title: test.value?.examName || 'Exam', url: `/app/exams/${test.value.examId}/subjects` })
        }
        if (test.value?.subjectId) {
          items.push({ title: test.value?.subjectName || 'Subject', url: `/app/subjects/${test.value.subjectId}/topics` })
        }
        if (test.value?.topicId) {
          items.push({ title: test.value?.topicName || 'Topic', url: `/app/topics/${test.value.topicId}/lessons` })
        }
        items.push({ title: test.value?.lessonTitle || 'Lesson', url: `/app/lessons/${test.value.lessonId}/tests` })
      } else if (test.value?.seriesId) {
        // Exam → TestSeries → Test → Questions
        if (test.value?.examId) {
          items.push({ title: test.value?.examName || 'Exam', url: `/app/exams/${test.value.examId}/test-series` })
        }
        items.push({ title: test.value?.seriesName || 'Test Series', url: `/app/test-series/${test.value.seriesId}/tests` })
      } else if (test.value?.topicId) {
        // Exam → Subject → Topic → Test → Questions
        if (test.value?.examId) {
          items.push({ title: test.value?.examName || 'Exam', url: `/app/exams/${test.value.examId}/subjects` })
        }
        if (test.value?.subjectId) {
          items.push({ title: test.value?.subjectName || 'Subject', url: `/app/subjects/${test.value.subjectId}/topics` })
        }
        items.push({ title: test.value?.topicName || 'Topic', url: `/app/topics/${test.value.topicId}/tests` })
      }
      items.push({ title: test.value?.title || 'Test', url: `/app/tests/${testId}/results` })
      items.push({ title: 'Questions', active: true })
      return items
    })

    const fetchData = async () => {
      try {
        const [testRes, questionsRes] = await Promise.all([
          api.get(`/v1/admin/tests/${testId}`),
          api.get(`/v1/admin/tests/${testId}/questions`)
        ])
        test.value = testRes.data.test
        questions.value = questionsRes.data.questions || []
      } catch (err) {
        console.error('Failed to fetch data:', err)
      }
    }

    const stripHtml = (html) => html?.replace(/<[^>]*>/g, '') || ''
    
    const truncate = (text, length) => {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }
    
    const formatType = (type) => {
      if (!type) return '-'
      const types = { mcq: 'MCQ', numerical: 'Numerical', match: 'Match', subjective: 'Subjective' }
      return types[type.toLowerCase()] || type
    }

    const getDifficultyClass = (difficulty) => {
      const classes = { easy: 'bg-success', medium: 'bg-warning', hard: 'bg-danger' }
      return classes[difficulty] || 'bg-secondary'
    }

    const resetForm = () => {
      form.question_type = 'mcq'
      form.content = ''
      form.option_a = ''
      form.option_b = ''
      form.option_c = ''
      form.option_d = ''
      form.correct_answer = ''
      form.explanation = ''
      form.shortcut_method = ''
      form.difficulty = 'medium'
      form.marks = 1
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openPopulateModal = () => {
      showPopulateModal.value = true
    }

    const onPopulateSuccess = () => {
      alert.success('Success!', 'Questions have been generated and added to the test.')
      fetchData()
    }

    const openEditModal = (question) => {
      isEditing.value = true
      editingId.value = question.id
      
      // Map fields from API response
      form.question_type = question.type || question.questionType || 'mcq'
      form.content = question.questionText || question.content || ''
      form.difficulty = question.difficulty || 'medium'
      
      // Parse answerMeta for additional fields
      let answerMeta = {}
      if (question.answerMeta) {
        try {
          answerMeta = typeof question.answerMeta === 'string' ? JSON.parse(question.answerMeta) : question.answerMeta
        } catch (e) {
          console.log('Failed to parse answerMeta:', e)
        }
      }
      
      form.marks = question.marks || answerMeta.marks || 1
      form.explanation = question.explanation || answerMeta.explanation || ''
      form.shortcut_method = question.shortcut_method || question.shortcutMethod || answerMeta.shortcut_method || ''
      form.correct_answer = question.correct_answer || question.correctAnswer || answerMeta.correct_answer || ''
      
      // Parse options if MCQ
      if (form.question_type === 'mcq') {
        let opts = null
        if (question.options) {
          try {
            opts = typeof question.options === 'string' ? JSON.parse(question.options) : question.options
          } catch (e) {
            console.log('Failed to parse options:', e)
          }
        }
        if (opts) {
          form.option_a = opts.a || opts.A || ''
          form.option_b = opts.b || opts.B || ''
          form.option_c = opts.c || opts.C || ''
          form.option_d = opts.d || opts.D || ''
        } else {
          form.option_a = ''
          form.option_b = ''
          form.option_c = ''
          form.option_d = ''
        }
      }
      
      showModal.value = true
    }

    const saveQuestion = async () => {
      if (!form.content || !form.question_type) return
      
      saving.value = true
      try {
        const payload = {
          content: form.content,
          question_type: form.question_type,
          difficulty: form.difficulty,
          marks: form.marks,
          explanation: form.explanation,
          shortcut_method: form.shortcut_method,
          correct_answer: form.correct_answer,
          test_id: testId,
          status: 'draft'
        }
        
        // Add options for MCQ
        if (form.question_type === 'mcq') {
          payload.options = JSON.stringify({
            a: form.option_a,
            b: form.option_b,
            c: form.option_c,
            d: form.option_d
          })
        }

        if (isEditing.value) {
          const response = await api.put(`/v1/admin/questions/${editingId.value}`, payload)
          const index = questions.value.findIndex(q => q.id === editingId.value)
          if (index !== -1) {
            questions.value[index] = { ...questions.value[index], ...response.data.question || payload }
          }
          alert.success('Updated!', 'Question updated successfully')
        } else {
          const response = await api.post('/v1/admin/questions', payload)
          questions.value.push(response.data.question || response.data)
          alert.success('Created!', 'Question created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        alert.error('Error', error.response?.data?.error?.message || 'Failed to save question')
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = async (q) => {
      const result = await alert.delete('Delete Question?')

      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/questions/${q.id}`)
          questions.value = questions.value.filter(item => item.id !== q.id)
          alert.success('Deleted!', 'Question has been deleted.')
        } catch (err) {
          alert.error('Error', 'Failed to delete question')
        }
      }
    }

    onMounted(fetchData)

    return { 
      testId, 
      test, 
      questions, 
      breadcrumbs, 
      showModal,
      showPopulateModal,
      isEditing,
      saving,
      form,
      editingId,
      parentType,
      parentId,
      parentName,
      stripHtml, 
      truncate,
      formatType,
      getDifficultyClass, 
      openCreateModal,
      openPopulateModal,
      onPopulateSuccess,
      openEditModal,
      saveQuestion,
      confirmDelete 
    }
  }
}
</script>

<style scoped>
.question-preview {
  cursor: help;
  max-width: 400px;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.options-section {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.option-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.option-label {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #6366f1;
  color: white;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .options-section {
  background: #0f172a;
  border-color: #334155;
}
</style>
